import styled from '@emotion/styled'
import { regexEmail } from 'mailup-react-helpers'
import {
  Checkbox,
  Chip,
  ChipIconTimes,
  ChipIconWrapper,
  Flex,
  FlexItem,
  IconWarningTriangle,
  INPUT_TEXT_VARIANT_DANGER,
  InputText,
  Link,
  LINK_VARIANT_EXTRALIGHT,
  Select,
  SPACING_8,
  SpacingVertical,
  Text,
  TEXT_VARIANT_ITALIC_LIGHT
} from 'mailup-web-components'
import {
  initGroup,
  verifyRecipientEmailsCallback
} from 'Pages/QuickSend/recoil/callbacks/callbacks'
import {
  addToGroupsLoadingState,
  addToGroupsState,
  recipientsState
} from 'Pages/QuickSend/recoil/state/state'
import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'

export const StyledIcon = styled(IconWarningTriangle)`
  color: #7c7c7c;
`

const StyledSelect = styled(Select)`
  width: 100%;
  .e-select__tray {
    max-height: 160px;
  }
`

const StyledCheckbox = styled(Checkbox)`
  .c-checkbox__label {
    color: var(--boulder);
    font-size: 14px;
  }
`

const InputRecipients = () => {
  const intl = useIntl()

  const options = useRecoilValue(addToGroupsState)
  const [
    { group, emailAddresses, emailAddress, check },
    setRecipientState
  ] = useRecoilState(recipientsState)
  const verifyRecipientEmails = useRecoilCallback(
    verifyRecipientEmailsCallback,
    []
  )
  const { loading, loaded, error } = useRecoilValue(addToGroupsLoadingState)

  const initRecipientGroup = useRecoilCallback(initGroup, [])

  const [validAddress, setValidAddress] = useState(true)

  const handleChange = useCallback(event => {
    const { value } = event.target
    setRecipientState(prev => ({
      ...prev,
      emailAddress: value
    }))
  })

  const getTokenEmails = () => {
    return emailAddress.indexOf(';') > -1
      ? emailAddress
          .split(';')
          .map(e => e.trim()) // caso particolare in cui l'utente inserisce spazi bianchi all'inizio o alla fine
          .filter(e => e !== '') // caso particolare in cui l'utente lascia il ; all'inizio o alla fine
      : emailAddress !== ''
      ? [emailAddress.trim()]
      : []
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      verifyRecipientEmails()
    }
  }

  const handleDeleteAddress = e => {
    const eList = [...emailAddresses]
    eList.splice(
      eList.findIndex(f => f === e),
      1
    )
    setRecipientState(prev => ({
      ...prev,
      emailAddresses: eList
    }))
  }

  const handleKeyUp = useCallback(e => {
    setValidAddress(
      getTokenEmails().find(e => e.match(regexEmail) === null) === undefined
    )
  })

  const handleCheckBoxChange = useCallback(e => {
    const val = e.target.value === 'true'
    setRecipientState(prev => ({
      ...prev,
      check: !val
    }))
  })

  const handleSelectChange = useCallback(e => {
    setRecipientState(prev => ({
      ...prev,
      group: e
    }))
  })

  const getHint = () => {
    return (
      options.length === 0 &&
      intl.formatMessage({
        id: 'Load.Common.NoResultChosen'
      })
    )
  }

  useEffect(() => {
    !loaded && initRecipientGroup()
  }, [loaded])

  return (
    <>
      <InputText
        variant={validAddress ? null : INPUT_TEXT_VARIANT_DANGER}
        placeholder=''
        value={emailAddress}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onKeyUp={handleKeyUp}
        data-mu-test='recipient-input-emails'
      />
      {emailAddresses && (
        <Flex margin='8px 0 0 0' rowGap='8px' columnGap='8px' flexWrap='wrap'>
          {emailAddresses.map(address => (
            <FlexItem key={address}>
              <Chip value={address} label={address}>
                <ChipIconWrapper
                  onClick={() => handleDeleteAddress(address)}
                  data-mu-test='recipient-emails-chip'
                >
                  <ChipIconTimes />
                </ChipIconWrapper>
              </Chip>
            </FlexItem>
          ))}
        </Flex>
      )}
      <SpacingVertical variant={SPACING_8} />
      <StyledCheckbox
        label={intl.formatMessage({ id: 'Load.QuickSend.AddToGroup' })}
        value={check}
        onChange={handleCheckBoxChange}
        checked={check}
      />

      {check && !error && (
        <Flex width='100%' margin='8px 0'>
          <StyledSelect
            loading={loading}
            disabled={loading}
            value={group}
            searchable
            onChange={handleSelectChange}
            options={options}
            hint={getHint()}
            placeholder={intl.formatMessage({ id: 'Load.Common.SelectGroup' })}
          />
        </Flex>
      )}
      {check && error && (
        <>
          <Flex
            margin='32px'
            columnGap='4px'
            height='24px'
            alignItems='flex-end'
          >
            <StyledIcon />
            <Text variant={TEXT_VARIANT_ITALIC_LIGHT} htmlElement='span'>
              {intl.formatMessage(
                {
                  id: 'Load.MailupCommon.ErrorFetchingReload'
                },
                {
                  link: chunks => (
                    <Link
                      onClick={initRecipientGroup}
                      variant={LINK_VARIANT_EXTRALIGHT}
                    >
                      {chunks}
                    </Link>
                  )
                }
              )}
            </Text>
          </Flex>
        </>
      )}
    </>
  )
}

export default InputRecipients
